import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Clock, Briefcase, Building2, FolderOpen, Loader2 } from "lucide-react";
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "./ui/command";
import { apiFetch } from "../lib/api";

const TYPE_META = {
  client: { label: "Clients", icon: Building2, color: "text-blue-500" },
  project: { label: "Projects", icon: FolderOpen, color: "text-violet-500" },
  work_order: { label: "Work Orders", icon: Briefcase, color: "text-brand-ink" },
  time_entry: { label: "Time Entries", icon: Clock, color: "text-emerald-500" },
};

/**
 * GlobalSearch — Command palette triggered by Ctrl+K / ⌘K or the search button.
 * Debounces API calls; groups results by resource type.
 */
const GlobalSearch = ({ open, onOpenChange }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const debounceRef = useRef(null);

  const search = useCallback(async (q) => {
    if (q.trim().length < 2) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const res = await apiFetch(`/search?q=${encodeURIComponent(q.trim())}&limit=30`);
      if (res.ok) {
        const data = await res.json();
        setResults(data.results || []);
      }
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Debounce query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => search(query), 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, search]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setQuery("");
      setResults([]);
    }
  }, [open]);

  const handleSelect = (url) => {
    onOpenChange(false);
    navigate(url);
  };

  // Group results by type
  const grouped = results.reduce((acc, r) => {
    if (!acc[r.type]) acc[r.type] = [];
    acc[r.type].push(r);
    return acc;
  }, {});

  const groupOrder = ["client", "project", "work_order", "time_entry"];

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput
        placeholder="Search clients, projects, work orders, time entries…"
        value={query}
        onValueChange={setQuery}
      />
      <CommandList>
        {loading && (
          <div className="flex items-center justify-center py-6 gap-2 text-sm text-brand-steel">
            <Loader2 className="w-4 h-4 animate-spin" />
            Searching…
          </div>
        )}

        {!loading && query.trim().length >= 2 && results.length === 0 && (
          <CommandEmpty>No results for "{query}"</CommandEmpty>
        )}

        {!loading && query.trim().length < 2 && (
          <div className="py-8 text-center text-sm text-brand-steel">
            Type at least 2 characters to search
          </div>
        )}

        {!loading && groupOrder.map((type, idx) => {
          const items = grouped[type];
          if (!items?.length) return null;
          const meta = TYPE_META[type];
          const Icon = meta.icon;
          return (
            <div key={type}>
              {idx > 0 && <CommandSeparator />}
              <CommandGroup heading={meta.label}>
                {items.map((item) => (
                  <CommandItem
                    key={`${type}:${item.id}`}
                    value={`${type} ${item.title} ${item.subtitle}`}
                    onSelect={() => handleSelect(item.url)}
                    className="flex items-start gap-3 py-2 cursor-pointer"
                  >
                    <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${meta.color}`} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-brand-ink truncate">{item.title}</p>
                      {item.subtitle && (
                        <p className="text-xs text-brand-steel truncate">{item.subtitle}</p>
                      )}
                    </div>
                    {item.status && (
                      <span className="text-xs text-brand-steel flex-shrink-0 capitalize">
                        {item.status.replace(/_/g, " ")}
                      </span>
                    )}
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          );
        })}
      </CommandList>
    </CommandDialog>
  );
};

export default GlobalSearch;
